import { useCallback, useEffect, useRef, useState } from "react";
import { Grid, Typography } from "@mui/material";

import style from "./styles/index.module.css";

import {
  IAddPatient,
  IAppointmentCreation,
  IAppointmetEnums,
  IPatientExistOrNotModule,
} from "@/models/doctor";
import {
  IAppointmentPermissionEnums,
  IModalKeyEnums,
  IScheduleSlotsPermissionEnums,
  IUserPermissionEnums,
} from "@/models/permission";
import { IOtpVerify } from "@/models/onboarding";
import { IRoleType } from "@/models/role";
import { ISessionKeys, IUniversalSearchFilterType } from "@/models";

import GetImages from "@/components/GetImages";

import useMobileCheckPatientCreate from "./useMobileCheckPatientCreate";
import useAppointmentDateFilter from "./useAppointmentDateFilter";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  resetPatientDetailErrorMessages,
  resetPatientExistOrNot,
  setModuleName,
} from "@/store/slices/doctor/checkMobilePatientExistOrNot";
import {
  resetTopNavigation,
  setAddPatient,
} from "@/store/slices/doctor/topNavigation";
import {
  fetchScheduleAppointmentList,
  resetScheduleSelection,
  setIsSelectionOn,
} from "@/store/slices/doctor/schedule";
import {
  makePatientAppointment,
  resetAppointment,
  setAppointmentDetail,
} from "@/store/slices/doctor/appointment";
import { setShowPermission } from "@/store/slices/permission";
import { universalSearchFilterDetails } from "@/store/slices/doctor/universalSearchFilter";

import useIsMobile from "./useIsMobile";
import useIsTablet from "./useIsTablet";
import { usePermissionRestriction } from "./usePermissionRestriction";
import { convertDateDDMMM } from "@/utils";
import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";
import { storage } from "@/utils/Storage";

const useMakeAppointment = () => {
  const [makeAppointment, setMakeAppointment] = useState<IAppointmentCreation>({
    creation: false,
    mobileCheck: false,
    otp: false,
    patientDetail: false,
    success: false,
    patientAlreadyExist: false,
  });
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [slotNo, setSlotNo] = useState<string>("");
  const [showDateRangeSelector, setShowDateRangeSelector] =
    useState<boolean>(false);
  const [showCancelSchedule, setShowCancelSchedule] = useState<boolean>(false);

  // Add permission states
  const [permissions, setPermissions] = useState({
    createPatient: false,
    otpCreatePatient: false,
    createAppointment: false,
    viewScheduleSlot: false,
  });

  const dateSelectorModalRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const { checkNumberError, moduleName } = useAppSelector(
    (state) => state.patientExistOrNot
  );
  const scheduleInfo = useAppSelector((state) => state.schedule);
  const { selectedAppointments, selectedSchedules } = useAppSelector(
    (state) => state.schedule.selection
  );
  const makePatientAppointmentDetail = useAppSelector(
    (state) => state.appointment.makePatientAppointment
  );
  const { schedule, reception, clinicAppointments } = useAppSelector(
    universalSearchFilterDetails
  );

  const roleType = storage.getSessionToken(ISessionKeys.ROLE_TYPE);

  const {
    checkMobileNumberDetail,
    createPatientAfterVerifyOtp,
    sendOtpToCreatePatient,
    otpId,
    setOtpId,
  } = useMobileCheckPatientCreate({
    mobileNo: makePatientAppointmentDetail?.mobileNo,
  });
  const { applyDateRangeValues } = useAppointmentDateFilter();

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const permissionRestrictionHandler = usePermissionRestriction();

  const openCloseAppointmentCancelConfirmation = () =>
    setShowCancelSchedule(!showCancelSchedule);

  const closeScheduleModal = () => {
    const schedulesValues: any = {};
    for (let key in makeAppointment) {
      schedulesValues[key] = false;
    }
    setMakeAppointment(schedulesValues);
  };

  // Initialize permissions once
  useEffect(() => {
    setPermissions({
      createPatient: permissionRestrictionHandler(
        IModalKeyEnums.USER,
        IUserPermissionEnums.CREATE_PATIENT_BY_DOCTOR
      ),
      otpCreatePatient: permissionRestrictionHandler(
        IModalKeyEnums.USER,
        IUserPermissionEnums.OTP_TO_CREATE_PATIENT_BY_DOCTOR
      ),
      createAppointment: permissionRestrictionHandler(
        IModalKeyEnums.APPOINTMENT,
        IAppointmentPermissionEnums.CREATE_APPOINTMENT
      ),
      viewScheduleSlot: permissionRestrictionHandler(
        IModalKeyEnums.SCHEDULE_SLOTS,
        IScheduleSlotsPermissionEnums.VIEW_SCHEDULE_SLOT_BY_DOCTOR
      ),
    });
  }, []);

  const openScheduleModalHandler = (type: IAppointmetEnums, value?: string) => {
    const schedulesValues: any = {};
    for (let key in makeAppointment) {
      schedulesValues[key] = false;
    }
    switch (type) {
      case IAppointmetEnums.CREATION:
        setMakeAppointment({
          ...schedulesValues,
          creation: true,
        });
        return;
      case IAppointmetEnums.MOBILE_CHECK:
        setMakeAppointment({
          ...schedulesValues,
          mobileCheck: true,
        });
        return;
      case IAppointmetEnums.PATIENT_DETAIL:
        checkMobileNumberDetail(
          value!,
          makePatientAppointmentDetail?.countryCode
        );
        return;
      case IAppointmetEnums.OTP:
        setMakeAppointment({
          ...schedulesValues,
          otp: true,
          patientDetail: false,
        });
        return;
      case IAppointmetEnums.SUCCESS:
        setMakeAppointment({
          ...schedulesValues,
          success: true,
        });
        setSlotNo(value!);
        return;
      default:
        closeScheduleModal();
        return;
    }
  };

  const navigateToMobileVerify = () => {
    if (!permissions.createPatient) return;

    dispatch(resetPatientDetailErrorMessages());
    setMakeAppointment({
      ...makeAppointment,
      otp: false,
      mobileCheck: true,
    });
  };

  const handleCreatePatientVerifyOTP = (value: IOtpVerify) => {
    if (!permissions.otpCreatePatient) return;

    createPatientAfterVerifyOtp(value.otp);
    setMakeAppointment({
      ...makeAppointment,
      mobileCheck: false,
      patientDetail: true,
    });
  };

  const createAppointmentHandler = () => {
    if (!permissions.createAppointment) return;
    if (!permissions.viewScheduleSlot) return;

    setMakeAppointment({
      creation: true,
      mobileCheck: false,
      otp: false,
      patientDetail: false,
      success: false,
      patientAlreadyExist: false,
    });
    dispatch(setModuleName(IPatientExistOrNotModule.APPOINTMENT));
  };

  const clearEnterPatientStates = () => {
    closeScheduleModal();
    setSlotNo("");
    dispatch(
      makePatientAppointment({ ...makePatientAppointmentDetail, mobileNo: "" })
    );
    dispatch(resetPatientDetailErrorMessages());
    dispatch(
      setAddPatient({
        field: IAddPatient.APPOINTMENT,
        value: false,
      })
    );
    dispatch(resetTopNavigation());
    dispatch(resetPatientExistOrNot());
    dispatch(resetAppointment());
    if (roleType === IRoleType.PHARMACY) {
      dispatch(fetchScheduleAppointmentList({}));
    }
  };

  const backMakeAppointmentHandler = (appointmentEnum: IAppointmetEnums) => {
    setMakeAppointment({
      ...makeAppointment,
      ...(appointmentEnum === IAppointmetEnums.CREATION
        ? { creation: true, mobileCheck: false }
        : appointmentEnum === IAppointmetEnums.MOBILE_CHECK
        ? { mobileCheck: true, patientDetail: false, otp: false }
        : {}),
    });
    setOtpId("");
    dispatch(resetPatientDetailErrorMessages());
  };

  const handleClickDateRangeOutside = (event: MouseEvent) => {
    if (
      dateSelectorModalRef.current &&
      !dateSelectorModalRef.current.contains(event.target as Node)
    ) {
      setShowDateRangeSelector(false);
    }
  };

  const openCloseDateRangeSelector = () => {
    if (showDateRangeSelector) {
      document.removeEventListener("mousedown", handleClickDateRangeOutside);
    } else {
      document.addEventListener("mousedown", handleClickDateRangeOutside);
    }
    setShowDateRangeSelector(!showDateRangeSelector);
  };

  const submitDateRangeValues = (start: string, end: string) => {
    applyDateRangeValues(start, end);
    openCloseDateRangeSelector();
    dispatch(setAppointmentDetail({}));
  };

  const appointmentFilterByDateRange = useCallback(
    (type: IUniversalSearchFilterType) => {
      const dateFilter =
        type == IUniversalSearchFilterType.SCHEDULE
          ? schedule
          : type == IUniversalSearchFilterType.CLINIC_APPOINTMENTS
          ? clinicAppointments
          : reception;

      return (
        <Grid
          item
          className={style.chooseDateFilter}
          onClick={openCloseDateRangeSelector}
        >
          <GetImages name="CalenderIcon" width="20" height="20" />
          {!(dateFilter?.startDate || dateFilter?.endDate) ? (
            <Typography component={"p"}>Choose a Date</Typography>
          ) : (
            <Typography component={"p"}>
              {dateFilter?.startDate === dateFilter?.endDate
                ? convertDateDDMMM(dateFilter?.startDate!)
                : `${convertDateDDMMM(
                    dateFilter?.startDate!
                  )} - ${convertDateDDMMM(dateFilter?.endDate!)}`}
            </Typography>
          )}
        </Grid>
      );
    },
    [schedule, reception, clinicAppointments]
  );

  const appointmentSelectionCancel = useCallback(() => {
    const cancelAppointmentHandler = () => {
      if (selectedSchedules?.length === 0 && selectedAppointments?.length === 0)
        return;
      openCloseAppointmentCancelConfirmation();
    };
    return (
      <Grid item className={style.selectionContainer}>
        <Typography
          component={"p"}
          className={style.selectText}
          onClick={() =>
            scheduleInfo?.selection?.isSelectionOn
              ? dispatch(resetScheduleSelection())
              : dispatch(setIsSelectionOn())
          }
        >
          {scheduleInfo?.selection?.isSelectionOn ? "Clear" : "Select"}
        </Typography>
        {scheduleInfo?.selection?.isSelectionOn && !(isMobile || isTablet) && (
          <Grid item className={style.cancelAppointmentEvent}>
            <GetImages name="CancelIcon" width="28" height="24" />
            <Typography component={"p"} onClick={cancelAppointmentHandler}>
              {" "}
              Cancel Selected
            </Typography>
          </Grid>
        )}
      </Grid>
    );
  }, [
    scheduleInfo?.selection?.isSelectionOn,
    selectedSchedules,
    selectedAppointments,
  ]);

  useEffect(() => {
    if (moduleName === IPatientExistOrNotModule.APPOINTMENT) {
      if (
        checkNumberError &&
        checkNumberError?.statusCode === IAPIStatusCode.BAD_REQUEST
      ) {
        if (!permissions.otpCreatePatient) {
          dispatch(setShowPermission(true));
          return;
        }
        openScheduleModalHandler(IAppointmetEnums.OTP);
        sendOtpToCreatePatient(makePatientAppointmentDetail?.countryCode);
        return;
      }
      if (
        checkNumberError &&
        checkNumberError?.statusCode === IAPIStatusCode.SUCCESS
      ) {
        if (!permissions.createPatient) return;

        setMakeAppointment({
          ...makeAppointment,
          mobileCheck: false,
          patientDetail: true,
          patientAlreadyExist: true,
        });
        return;
      }
    }
  }, [checkNumberError, moduleName, permissions]);

  return {
    makeAppointment,
    setMakeAppointment,
    slotNo,
    openScheduleModalHandler,
    navigateToMobileVerify,
    handleCreatePatientVerifyOTP,
    createAppointmentHandler,
    sendOtpToCreatePatient,
    otpId,
    clearEnterPatientStates,
    selectedTab,
    setSelectedTab,
    backMakeAppointmentHandler,
    appointmentFilterByDateRange,
    showDateRangeSelector,
    submitDateRangeValues,
    appointmentSelectionCancel,
    showCancelSchedule,
    openCloseAppointmentCancelConfirmation,
    dateSelectorModalRef,
  };
};

export default useMakeAppointment;
